import { jezikBrojcanika } from '../lib/format'

/**
 * NATIVNO POLJE SE NE PONAŠA KAO POLJE (dotjerivanje-4 §1 i §3).
 *
 * `date`, `time` i `datetime-local` ne crta naš CSS nego preglednik, iz vlastitog
 * sjenovitog stabla — i to je jedina vrsta polja u obrascu koja ne sluša `w-full`
 * do kraja. WebKit im daje unutarnju širinu po SADRŽAJU (kalendar, brojčanik,
 * ikona), pa je gostov obrazac stajao poravnat po lijevom rubu, a datum i vrijeme
 * su za koji piksel virili udesno — dovoljno da se kolona vidi kao neuredna, a
 * premalo da se odmah vidi zašto.
 *
 * Dva zahvata, oba na jednom mjestu:
 *
 * · **`appearance-none`** gasi nativno mjerenje i vraća polje u naš okvir; ide
 *   SAMO ovim vrstama, jer bi ista klasa na `checkbox` ili `<select>` obrisala
 *   kvačicu i strelicu.
 * · **`min-w-0 max-w-full`** je brana: `min-width: auto` pušta polje da naraste
 *   preko roditelja, a ovo mu kaže da je rub kolone rub i za njega.
 *
 * `lang` je treći dio istog posla, ali druga tema — brojčanik mora biti 24-satni
 * (`lib/format.ts`), pa se odluka čita odande.
 *
 * Pitaju ga OBA kompleta polja: `noir.tsx` (gost i vozač) i `Field.tsx` (konzola).
 * Da svaki sudi sam, prva razlika bila bi obrazac koji se poravnava na jednom
 * sučelju, a na drugom ne.
 */
const NATIVNA_SATNA = ['date', 'time', 'datetime-local', 'month', 'week']

/**
 * MJERE POLJA — JEDAN IZVOR ZA OBA KOMPLETA (D15-A §3).
 *
 * Boje su razlika između konzole (paper/ink) i noira (surface/ivory); MJERE
 * nisu. Dok je svaki komplet pisao svoje, razlika se vidjela ondje gdje polja
 * stoje jedno uz drugo — u "Mom autu" je izbor države bio ZAMJETNO viši od
 * polja s registracijom pored njega, i to nije bio propust nego posljedica: obje
 * su kontrole nosile `py-2.5` i `touch-target`, ali `<select>` visinu računa iz
 * vlastitog sjenovitog stabla (strelica, unutarnji razmak), pa je `min-height`
 * mogao samo podignuti, nikad izjednačiti.
 *
 * Zato se visina zadaje IZRIJEKOM (`h-11`, `h-14`), a ne kroz `min-height`:
 * `<input>` i `<select>` tada stoje na istoj crti bez obzira na to što ispod
 * crta preglednik. Padding ostaje jer ga trebaju `<select>`ov tekst i
 * `placeholder`.
 *
 * Višeredno polje je iznimka i jedino nosi `min-h-`: `<textarea>` mora smjeti
 * narasti.
 */
export const MJERA_POLJA = 'touch-target h-11 w-full rounded-lg border px-3 py-2 text-sm'

/**
 * KRUPNA ODLUKA (CLAUDE.md §13, §17): polje kroz koje se BIRA — stanica, termin,
 * vrsta, soba — nosi veću metu, deblji obrub i slovo od 17 px. Granica je bez
 * iznimke: ispod 16 px iOS pri fokusu zumira cijeli ekran.
 */
export const MJERA_KRUPNO = 'touch-target h-14 w-full rounded-xl border-2 px-4 py-3 text-[17px]'

/** Višeredno polje: raste, pa mu visina stoji kao DONJA granica. */
export const MJERA_VISEREDNO = 'min-h-24 w-full rounded-lg border px-3 py-2 text-sm'

export interface NativnoPolje {
  /** Jezik brojčanika; `undefined` za polja koja ga nemaju. */
  lang: string | undefined
  /** Dodatne klase; prazan string za obična polja. */
  razred: string
}

export function nativnoPolje(vrsta: string | undefined): NativnoPolje {
  return {
    lang: jezikBrojcanika(vrsta),
    razred: vrsta && NATIVNA_SATNA.includes(vrsta) ? 'appearance-none min-w-0 max-w-full' : '',
  }
}
